import { useState, useCallback } from "react"
import { useAuth } from "services/auth/auth-service-hooks"
import { useUserContext } from "context/UserContext"
import { FetchApiProps } from "../types/api"

interface FetchOptions {
    body?: object
    returnFullResponse?: boolean
}

export type FetchFullResponse<T = unknown> = {
    data: T | null
    ok: boolean
    status: number
    error: object | null
}
export const useFetch = <T = unknown>(
    url: string,
    options: FetchOptions = {}
): FetchApiProps<T> => {
    const [data, setData] = useState<T | null>(null)
    const [status, setStatus] = useState<null | number>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean | null>(null)
    const [error, setError] = useState<object | null>(null)
    const { getAccessTokenSilently } = useAuth()
    const { emsp_partner_id } = useUserContext()


    const sendRequest = useCallback(
        async (
            body: object | null = null
        ): Promise<T | null | FetchFullResponse<T>> => {
            setIsLoading(true)
            setError(null)
            let parsedData: T | null = null

            try {
                const accessToken = await getAccessTokenSilently()
                let headers: Record<string, string> = {
                    "content-type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                }
                if (typeof window !== "undefined") {
                    try {
                        const orgId = sessionStorage.getItem("organizationId")
                        if (orgId && orgId !== emsp_partner_id) {
                            headers = { ...headers, "X-organization-id": orgId }
                        }
                    } catch {
                        // ignore storage access errors
                    }
                }
                const { body: bodyOptions } = options
                let payload = {}
                if (body) payload = { ...body }
                if (bodyOptions) payload = { ...payload, ...bodyOptions }
                const fetchOptions =
                    body || bodyOptions
                        ? { ...options, body: JSON.stringify(payload) }
                        : options
                const response = await fetch(url, {
                    ...fetchOptions,
                    headers,
                    body:
                        typeof fetchOptions.body === "object"
                            ? JSON.stringify(fetchOptions.body)
                            : fetchOptions.body,
                })
                setStatus(response.status)

                if (!response.ok) {
                    const error = new Error(`HTTP error! Status: ${response.status}`)

                    console.error(error)
                    setSuccess(false)
                    setError(error as object)

                    if (options.returnFullResponse) {
                        return {
                            data: null,
                            ok: false,
                            status: response.status,
                            error: error as object,
                        }
                    }

                    return null
                } else {
                    const contentType = response.headers.get("content-type")
                    if (contentType && contentType.includes("text/csv")) {
                        const contentDisposition = response.headers.get("content-disposition")
                        let filename = ""
                        if (contentDisposition) {
                            const parts = contentDisposition.split("filename=")
                            if (parts.length > 1) {
                                filename = parts[1].trim().replace(/(^"|"$)/g, "")
                            }
                        }

                        const blob = await response.blob()
                        const downloadUrl = window.URL.createObjectURL(blob)
                        const a = document.createElement("a")
                        a.href = downloadUrl
                        a.download = filename
                        document.body.appendChild(a)
                        a.click()
                        a.remove()
                        window.URL.revokeObjectURL(downloadUrl)
                        setData(null)
                        parsedData = null
                    } else {
                        const data = (await response.json()) as T
                        setData(data as T)
                        parsedData = data as T
                    }
                    setSuccess(true)

                    if (options.returnFullResponse) {
                        return {
                            data: parsedData,
                            ok: true,
                            status: response.status,
                            error: null,
                        }
                    }
                }
            } catch (err) {
                setSuccess(false)
                setError(err as object)
                console.error("Error fetching data:", err)
                parsedData = null
            } finally {
                setIsLoading(false)
            }
            return parsedData
        },
        [url, options, getAccessTokenSilently, emsp_partner_id]
    )

    return { data, isLoading, error, success, status, sendRequest }
}
